import "./Testimonials.css";
import { motion } from "framer-motion";
import StarIcon from "@mui/icons-material/Star";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

type Testimonial = {
  name: string;
  initials: string;
  rating: number;
  text: string;
  gradient: string;
};

const row1: Testimonial[] = [
  {
    name: "Sophia Rocha",
    initials: "SR",
    rating: 5,
    text: "Nunca imaginei que gestão de coworking pudesse ser tão simples. O Cowonder resolveu em minutos o que antes levava dias.",
    gradient: "from-[#490f70] to-[#791cb8]",
  },
  {
    name: "Kelvin Harena",
    initials: "KH",
    rating: 5,
    text: "O dashboard é absurdamente bom. Tenho tudo que preciso na tela: receita, ocupação, contratos. Decisão certeira assinar.",
    gradient: "from-[#791cb8] to-[#cb88f9]",
  },
  {
    name: "Marina Silva",
    initials: "MS",
    rating: 5,
    text: "As assinaturas digitais já valeram o plano inteiro no primeiro mês. Clientes adoraram a praticidade, eu adorei a economia.",
    gradient: "from-[#5b1796] to-[#490f70]",
  },
];

const row2: Testimonial[] = [
  {
    name: "João Carlos",
    initials: "JC",
    rating: 5,
    text: "Suporte respondeu em menos de 40 minutos num sábado à noite. Esse nível de atenção é raro demais. Produto e equipe incríveis.",
    gradient: "from-[#cb88f9] to-[#791cb8]",
  },
  {
    name: "Aldiberg Jr",
    initials: "AJ",
    rating: 5,
    text: "Automatizei cobranças, contratos e notificações de uma vez. Minha equipe encolheu pela metade e a receita cresceu 30%.",
    gradient: "from-[#491cb8] to-[#cb88f9]",
  },
  {
    name: "Guilherme Alves",
    initials: "GA",
    rating: 5,
    text: "Fiz a migração do zero em menos de um dia. A integração com meu financeiro foi plug-and-play. Recomendo sem hesitar.",
    gradient: "from-[#6b1fa8] to-[#3d0d5f]",
  },
];

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <div
      className="relative flex-shrink-0 w-[360px] mx-3 rounded-2xl p-[1px] overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(203,136,249,0.3) 0%, rgba(73,15,112,0.1) 100%)",
      }}
    >
      <div
        className="h-full rounded-2xl p-7 flex flex-col gap-5"
        style={{
          background: "rgba(15, 5, 30, 0.75)",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* Aspas decorativas */}
        <FormatQuoteIcon
          sx={{ fontSize: 42, color: "#cb88f9", opacity: 0.25, transform: "scaleX(-1)", mb: -2, ml: -1 }}
        />

        {/* Stars */}
        <div className="flex gap-0.5">
          {Array.from({ length: t.rating }).map((_, i) => (
            <StarIcon key={i} sx={{ fontSize: 16, color: "#f4be45" }} />
          ))}
        </div>

        {/* Texto */}
        <p className="text-white/75 text-sm leading-relaxed flex-1">"{t.text}"</p>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#cb88f9]/30 to-transparent" />

        {/* Autor */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
            <span className="text-white text-xs font-bold">{t.initials}</span>
          </div>
          <div>
            <p className="text-white text-sm font-bold leading-tight">{t.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const doubled1 = [...row1, ...row1];
  const doubled2 = [...row2, ...row2];

  return (
    <section className="relative py-32 overflow-hidden" style={{ background: "#08001a" }}>
      {/* Aurora blobs */}
      <div
        className="absolute -top-60 left-1/4 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(121,28,184,0.35) 0%, transparent 70%)", filter: "blur(80px)" }}
      />
      <div
        className="absolute -bottom-60 right-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(73,15,112,0.4) 0%, transparent 70%)", filter: "blur(80px)" }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[300px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(203,136,249,0.06) 0%, transparent 70%)", filter: "blur(40px)" }}
      />

      {/* Header */}
      <motion.div
        className="relative z-10 text-center px-6 mb-20"
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <span className="inline-block text-[#cb88f9] text-xs font-bold uppercase tracking-widest bg-[#cb88f9]/10 border border-[#cb88f9]/20 px-5 py-2 rounded-full mb-6">
          O que dizem nossos clientes
        </span>
        <h2
          className="font-extrabold text-5xl md:text-6xl leading-tight"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #cb88f9 50%, #791cb8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Quem usa,<br />não abandona.
        </h2>
        <p className="text-white/40 text-base mt-5 max-w-md mx-auto leading-relaxed">
          Mais de 500 empresas confiam no Cowonder para crescer todos os meses.
        </p>
      </motion.div>

      {/* Marquee row 1 — esquerda */}
      <div className="relative z-10 mb-5 overflow-hidden">
        {/* Fade lateral esquerdo */}
        <div className="absolute left-0 top-0 w-32 h-full z-20 pointer-events-none"
          style={{ background: "linear-gradient(to right, #08001a, transparent)" }} />
        {/* Fade lateral direito */}
        <div className="absolute right-0 top-0 w-32 h-full z-20 pointer-events-none"
          style={{ background: "linear-gradient(to left, #08001a, transparent)" }} />

        <div className="flex marquee-left">
          {doubled1.map((t, i) => <TestimonialCard key={`r1-${i}`} t={t} />)}
        </div>
      </div>

      {/* Marquee row 2 — direita */}
      <div className="relative z-10 overflow-hidden">
        <div className="absolute left-0 top-0 w-32 h-full z-20 pointer-events-none"
          style={{ background: "linear-gradient(to right, #08001a, transparent)" }} />
        <div className="absolute right-0 top-0 w-32 h-full z-20 pointer-events-none"
          style={{ background: "linear-gradient(to left, #08001a, transparent)" }} />

        <div className="flex marquee-right">
          {doubled2.map((t, i) => <TestimonialCard key={`r2-${i}`} t={t} />)}
        </div>
      </div>

      {/* Contador bottom */}
      <motion.div
        className="relative z-10 flex items-center justify-center gap-12 mt-20 px-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: "easeOut", delay: 0.2 }}
        viewport={{ once: true }}
      >
      </motion.div>
    </section>
  );
}
